// web/src/components/Scans/Scans.tsx
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { scansAPI } from '../../services/api';
import { Scan, Project } from '../../types';
import { useScanContext } from '../../contexts/ScanContext';
import ScanComparison from './ScanComparison';
import RiskGradeBadge from '../Common/RiskGradeBadge';
import './Scans.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const Scans: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { selectedScan, setSelectedScan } = useScanContext();
  const [scans, setScans] = useState<Scan[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState<{scanId: number, type: 'html' | 'json'} | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; scan: Scan | null }>({ show: false, scan: null });
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'warning'; message: string} | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [scanTypeFilter, setScanTypeFilter] = useState<string>('all');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Bulk selection state
  const [selectedScans, setSelectedScans] = useState<Set<number>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  // Debounce search input (400ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadScans();
  }, [projectId, page, pageSize, statusFilter, projectFilter, scanTypeFilter, debouncedSearch]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, projectFilter, scanTypeFilter, debouncedSearch]);

  const loadScans = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: pageSize,
      };

      // Add filters if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (projectFilter !== 'all') {
        params.project_name = projectFilter;
      }
      if (scanTypeFilter !== 'all') {
        params.scan_type = scanTypeFilter;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (projectId) {
        // Load scans for specific project
        const response = await scansAPI.getByProject(Number(projectId), params);
        const data = response.data;
        if (data.scans) {
          setScans(data.scans);
          // Handle both pagination formats (pagination.total or data.total)
          const total = data.pagination?.total || data.total || data.scans.length;
          const pages = data.pagination?.totalPages || data.total_pages || Math.ceil(total / pageSize);
          setTotalCount(total);
          setTotalPages(pages);
        } else if (Array.isArray(data)) {
          setScans(data);
          setTotalCount(data.length);
          setTotalPages(Math.ceil(data.length / pageSize));
        } else {
          setScans([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      } else {
        // Load all scans with pagination
        const response = await scansAPI.getAll(params);
        const data = response.data;

        // Handle both paginated and non-paginated responses
        let scansList: any[];
        if (data.scans) {
          scansList = data.scans;
          setTotalCount(data.total || scansList.length);
          setTotalPages(data.total_pages || Math.ceil((data.total || scansList.length) / pageSize));
        } else if (Array.isArray(data)) {
          scansList = data;
          setTotalCount(scansList.length);
          setTotalPages(1);
        } else {
          scansList = [];
          setTotalCount(0);
          setTotalPages(1);
        }

        // Extract unique projects for the filter dropdown
        const uniqueProjects: Project[] = [];
        const projectMap = new Map<number, Project>();

        scansList.forEach((scan: any) => {
          if (scan.project && !projectMap.has(scan.project.id)) {
            projectMap.set(scan.project.id, scan.project);
            uniqueProjects.push(scan.project);
          }
        });

        setProjects(uniqueProjects);

        // Add project_name to each scan for filtering
        const scansWithProjectName = scansList.map((scan: any) => ({
          ...scan,
          project_name: scan.project?.name || 'Unknown'
        }));

        setScans(scansWithProjectName);
      }
    } catch (error) {
      console.error('Failed to load scans:', error);
      showNotification('error', 'Failed to load scans');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const toggleScanSelection = (scanId: number) => {
    setSelectedScans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scanId)) {
        newSet.delete(scanId);
      } else {
        newSet.add(scanId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedScans.size === filteredScans.length) {
      setSelectedScans(new Set());
    } else {
      setSelectedScans(new Set(filteredScans.map(scan => scan.id)));
    }
  };

  const handleBulkDelete = async () => {
    const scanIds = Array.from(selectedScans);
    setActionLoading(-1); // Use -1 to indicate bulk action

    try {
      // Delete scans one by one (we'll create a bulk endpoint later)
      let deletedCount = 0;
      for (const scanId of scanIds) {
        try {
          await scansAPI.delete(scanId);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete scan ${scanId}:`, error);
        }
      }

      showNotification('success', `Successfully deleted ${deletedCount} of ${scanIds.length} scans`);
      setSelectedScans(new Set());
      setShowBulkDeleteConfirm(false);
      await loadScans(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to delete scans:', error);
      showNotification('error', 'Failed to delete scans');
    } finally {
      setActionLoading(null);
    }
  };

  const handleScanAction = async (scanId: number, action: 'start' | 'pause' | 'resume' | 'stop') => {
    setActionLoading(scanId);
    try {
      let response;
      switch (action) {
        case 'start':
          response = await scansAPI.start(scanId);
          break;
        case 'pause':
          response = await scansAPI.pause(scanId);
          break;
        case 'resume':
          response = await scansAPI.resume(scanId);
          break;
        case 'stop':
          response = await scansAPI.stop(scanId);
          break;
      }
      
      showNotification('success', response.data.message);
      await loadScans(); // Refresh the list
    } catch (error: any) {
      console.error(`Failed to ${action} scan:`, error);
      showNotification('error', error.response?.data?.error || `Failed to ${action} scan`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteScan = async () => {
    if (!deleteModal.scan) return;

    const scanId = deleteModal.scan.id;
    setActionLoading(scanId);
    
    try {
      await scansAPI.delete(scanId);
      showNotification('success', `Scan "${deleteModal.scan.name}" deleted successfully`);
      setDeleteModal({ show: false, scan: null });
      await loadScans(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to delete scan:', error);
      showNotification('error', error.response?.data?.error || 'Failed to delete scan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportHTML = async (scan: Scan, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (scan.status !== 'completed') {
      showNotification('warning', 'Can only export completed scans');
      return;
    }
    
    setExportLoading({ scanId: scan.id, type: 'html' });
    try {
      const response = await scansAPI.exportEnhancedHTML(scan.id);
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `enhanced_scan_${scan.id}_report.html`;
      link.click();
      window.URL.revokeObjectURL(url);
      showNotification('success', 'Enhanced HTML report exported successfully');
    } catch (error: any) {
      console.error('Failed to export HTML:', error);
      showNotification('error', error.response?.data?.error || 'Failed to export HTML report');
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportJSON = async (scan: Scan, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (scan.status !== 'completed') {
      showNotification('warning', 'Can only export completed scans');
      return;
    }
    
    setExportLoading({ scanId: scan.id, type: 'json' });
    try {
      const response = await scansAPI.exportJSON(scan.id);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan_${scan.id}_data.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      showNotification('success', 'JSON data exported successfully');
    } catch (error: any) {
      console.error('Failed to export JSON:', error);
      showNotification('error', error.response?.data?.error || 'Failed to export JSON data');
    } finally {
      setExportLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'var(--color-warning)';
      case 'running': return 'var(--color-primary-500)';
      case 'paused': return 'var(--color-purple-text)';
      case 'completed': return 'var(--color-success)';
      case 'failed': return 'var(--color-error)';
      case 'stopped': return 'var(--color-text-tertiary)';
      case 'cancelled': return 'var(--color-text-tertiary)';
      case 'interrupted': return 'var(--color-warning)';
      default: return 'var(--color-text-tertiary)';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return 'fas fa-clock';
      case 'running': return 'fas fa-spinner fa-spin';
      case 'paused': return 'fas fa-pause';
      case 'completed': return 'fas fa-check-circle';
      case 'failed': return 'fas fa-exclamation-circle';
      case 'stopped': return 'fas fa-stop-circle';
      case 'cancelled': return 'fas fa-times-circle';
      case 'interrupted': return 'fas fa-bolt';
      default: return 'fas fa-question-circle';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionButtons = (scan: Scan) => {
    const isLoading = actionLoading === scan.id;
    const isExportingHTML = exportLoading?.scanId === scan.id && exportLoading?.type === 'html';
    const isExportingJSON = exportLoading?.scanId === scan.id && exportLoading?.type === 'json';
    
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {/* Export Buttons - Show for completed scans */}
        {scan.status === 'completed' && (
          <>
            {/* Enhanced HTML Report Export Button */}
            <button
              onClick={(e) => handleExportHTML(scan, e)}
              disabled={isExportingHTML || isExportingJSON}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: isExportingHTML ? 'var(--color-text-muted)' : 'var(--color-success)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '4px',
                cursor: isExportingHTML || isExportingJSON ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}
              title="Export Enhanced HTML Report"
            >
              {isExportingHTML ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-chart-bar"></i>
              )}
            </button>

            {/* JSON Export Button */}
            <button
              onClick={(e) => handleExportJSON(scan, e)}
              disabled={isExportingHTML || isExportingJSON}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: isExportingJSON ? 'var(--color-text-muted)' : 'var(--color-primary-500)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '4px',
                cursor: isExportingHTML || isExportingJSON ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}
              title="Export JSON Data"
            >
              {isExportingJSON ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-code"></i>
              )}
            </button>
          </>
        )}

        {/* View Details Button - Always show as eye icon */}
        <Link
          to={projectId ? `/projects/${projectId}/scans/${scan.id}` : `/scans/${scan.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (projectId) {
              setSelectedScan(scan);
            }
          }}
          style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '4px',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.75rem'
          }}
          title="View Scan Details"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
            e.currentTarget.style.borderColor = 'var(--color-text-muted)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-background)';
            e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
          }}
        >
          <i className="fas fa-eye"></i>
        </Link>
        {/* Quick Action Buttons */}
        {scan.status === 'pending' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleScanAction(scan.id, 'start');
            }}
            disabled={isLoading}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: isLoading ? 'var(--color-text-muted)' : 'var(--color-success)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.75rem',
              fontWeight: '500'
            }}
            title="Start Scan"
          >
            {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-play"></i>}
          </button>
        )}
        
        {scan.status === 'running' && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleScanAction(scan.id, 'pause');
              }}
              disabled={isLoading}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: isLoading ? 'var(--color-text-muted)' : 'var(--color-purple-text)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem'
              }}
              title="Pause Scan"
            >
              {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-pause"></i>}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleScanAction(scan.id, 'stop');
              }}
              disabled={isLoading}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: isLoading ? 'var(--color-text-muted)' : 'var(--color-error)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem'
              }}
              title="Stop Scan"
            >
              {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-stop"></i>}
            </button>
          </>
        )}
        
        {(scan.status === 'paused' || scan.status === 'interrupted') && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleScanAction(scan.id, 'resume');
            }}
            disabled={isLoading}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: isLoading ? 'var(--color-text-muted)' : 'var(--color-success)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.75rem'
            }}
            title="Resume Scan"
          >
            {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-play"></i>}
          </button>
        )}

        {/* Delete Button - Available for non-running scans */}
        {!['running'].includes(scan.status) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ show: true, scan });
            }}
            disabled={isLoading}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-critical)',
              border: '1px solid var(--color-tint-red)',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.75rem'
            }}
            title="Delete Scan"
            onMouseOver={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = 'var(--color-tint-red)';
                e.currentTarget.style.borderColor = 'var(--color-critical)';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = 'var(--color-background)';
                e.currentTarget.style.borderColor = 'var(--color-tint-red)';
              }
            }}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        )}
      </div>
    );
  };

  // All filtering is now done server-side, no client-side filtering needed
  const filteredScans = scans;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: 'var(--color-primary-500)', marginBottom: '1rem' }}></i>
          <div>Loading scans...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          backgroundColor: notification.type === 'success' ? 'var(--color-tint-green)' :
                         notification.type === 'warning' ? 'var(--color-tint-yellow)' : 'var(--color-tint-red)',
          border: `1px solid ${notification.type === 'success' ? 'var(--color-tint-green)' :
                              notification.type === 'warning' ? 'var(--color-tint-yellow)' : 'var(--color-tint-red)'}`,
          color: notification.type === 'success' ? 'var(--color-success-dark)' :
                 notification.type === 'warning' ? 'var(--color-warning-dark)' : 'var(--color-critical)',
          zIndex: 1080,
          maxWidth: '400px',
          boxShadow: 'var(--color-card-shadow)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className={
              notification.type === 'success' ? 'fas fa-check-circle' : 
              notification.type === 'warning' ? 'fas fa-exclamation-triangle' : 
              'fas fa-exclamation-circle'
            }></i>
            {notification.message}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.scan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--color-overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-background)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: 'var(--color-card-shadow)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-tint-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ color: 'var(--color-critical)', fontSize: '1.5rem' }}></i>
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                Delete Scan
              </h3>
              <p style={{ color: 'var(--color-text-tertiary)', margin: 0, lineHeight: '1.5' }}>
                Are you sure you want to delete "<strong>{deleteModal.scan.name}</strong>"?
                This will permanently remove the scan and all its results. This action cannot be undone.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteModal({ show: false, scan: null })}
                disabled={actionLoading === deleteModal.scan.id}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteScan}
                disabled={actionLoading === deleteModal.scan.id}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: actionLoading === deleteModal.scan.id ? 'var(--color-text-muted)' : 'var(--color-critical)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionLoading === deleteModal.scan.id ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading === deleteModal.scan.id ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i>
                    Delete Scan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', color: 'var(--color-text-primary)' }}>
            {projectId ? t('scans.projectScans', 'Project Scans') : t('scans.allScans', 'All Scans')} ({totalCount > 0 ? `${filteredScans.length} ${t('common.of', 'of')} ${totalCount}` : filteredScans.length})
          </h1>
          <p style={{ color: 'var(--color-text-tertiary)', margin: 0, fontSize: '0.875rem' }}>
            {projectId ? t('scans.securityScans', 'Security scans for this project') : t('scans.securityScansAllProjects', 'Security scans across all projects')}
          </p>
        </div>

        {projectId && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link
              to={`/projects/${projectId}/discovery`}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-warning)',
                color: 'var(--color-text-inverse)',
                textDecoration: 'none',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-warning-dark)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-warning)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="fas fa-radar"></i>
              Discovery
            </Link>
            <Link
              to={`/projects/${projectId}/scans/new`}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-primary-500)',
                color: 'var(--color-text-inverse)',
                textDecoration: 'none',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
              }}
            >
              <i className="fas fa-plus"></i>
              New Scan
            </Link>
            <Link
              to={`/projects/${projectId}/client-reviews`}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-success)',
                color: 'var(--color-text-inverse)',
                textDecoration: 'none',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success-dark)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="fas fa-clipboard-check"></i>
              Client Reviews
            </Link>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedScans.size > 0 && (
        <div style={{
          backgroundColor: 'var(--color-tint-blue)',
          border: '1px solid var(--color-primary-500)',
          borderRadius: '12px',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontWeight: '600', color: 'var(--color-primary-700)' }}>
              {selectedScans.size} scan{selectedScans.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedScans(new Set())}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary-500)',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem'
              }}
            >
              Clear selection
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {/* Compare Button - only show when 2+ completed scans selected */}
            {selectedScans.size >= 2 && Array.from(selectedScans).every(id =>
              filteredScans.find(s => s.id === id)?.status === 'completed'
            ) && (
              <button
                onClick={() => setShowComparison(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-purple-text)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <i className="fas fa-chart-line"></i>
                Compare Scans ({selectedScans.size})
              </button>
            )}
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={actionLoading === -1}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: actionLoading === -1 ? 'var(--color-text-muted)' : 'var(--color-critical)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '8px',
                cursor: actionLoading === -1 ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {actionLoading === -1 ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i>
                  Delete Selected
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--color-overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-background)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: 'var(--color-card-shadow)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-tint-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ color: 'var(--color-critical)', fontSize: '1.5rem' }}></i>
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                Delete {selectedScans.size} Scan{selectedScans.size > 1 ? 's' : ''}
              </h3>
              <p style={{ color: 'var(--color-text-tertiary)', margin: 0, lineHeight: '1.5' }}>
                Are you sure you want to delete <strong>{selectedScans.size} scan{selectedScans.size > 1 ? 's' : ''}</strong>?
                This will permanently remove the scans and all their results. This action cannot be undone.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={actionLoading === -1}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={actionLoading === -1}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: actionLoading === -1 ? 'var(--color-text-muted)' : 'var(--color-critical)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionLoading === -1 ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading === -1 ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i>
                    Delete Scans
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{
        backgroundColor: 'var(--color-background)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Select All Checkbox */}
        {filteredScans.length > 0 && (
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={selectedScans.size === filteredScans.length && filteredScans.length > 0}
              onChange={toggleSelectAll}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', cursor: 'pointer' }} onClick={toggleSelectAll}>
              {t('scans.selectAll', 'Select all')} ({filteredScans.length} {t('scans.title', 'scans')})
            </label>
          </div>
        )}

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr auto auto' }}>
          {/* Search Input */}
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)',
              fontSize: '0.875rem'
            }}></i>
            <input
              type="text"
              placeholder={t('scans.searchScansPlaceholder', 'Search scans by name, description, or project...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary-500)';
                e.currentTarget.style.boxShadow = '0 0 0 3px var(--color-tint-blue)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.75rem 2.5rem 0.75rem 1rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                backgroundColor: 'var(--color-background)',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
            >
              <option value="all">{t('scans.allStatuses', 'All Statuses')}</option>
              <option value="pending">{t('scans.pending', 'Pending')}</option>
              <option value="running">{t('scans.running', 'Running')}</option>
              <option value="paused">{t('scans.paused', 'Paused')}</option>
              <option value="completed">{t('scans.completed', 'Completed')}</option>
              <option value="failed">{t('scans.failed', 'Failed')}</option>
              <option value="stopped">{t('scans.stopped', 'Stopped')}</option>
              <option value="cancelled">{t('scans.cancelled', 'Cancelled')}</option>
              <option value="interrupted">{t('scans.interrupted', 'Interrupted')}</option>
            </select>
          </div>

          {/* Project Filter (only show when viewing all scans) */}
          {!projectId && projects.length > 0 && (
            <div>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                style={{
                  padding: '0.75rem 2.5rem 0.75rem 1rem',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--color-background)',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="all">{t('scans.allProjects', 'All Projects')}</option>
                {projects.map(project => (
                  <option key={project.id} value={project.name}>{project.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Scan Type Filter */}
          <div>
            <select
              value={scanTypeFilter}
              onChange={(e) => setScanTypeFilter(e.target.value)}
              style={{
                padding: '0.75rem 2.5rem 0.75rem 1rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                backgroundColor: 'var(--color-background)',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
            >
              <option value="all">{t('scans.allScanTypes', 'All Scan Types')}</option>
              <option value="vulnerability">{t('scans.vulnerabilityScan', 'Vulnerability Scan')}</option>
              <option value="recon">{t('scans.reconScan', 'Recon Scan')}</option>
              <option value="discovery">{t('scans.networkDiscovery', 'Network Discovery')}</option>
            </select>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(searchTerm || statusFilter !== 'all' || projectFilter !== 'all' || scanTypeFilter !== 'all') && (
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>{t('scans.activeFilters', 'Active filters')}:</span>
            {searchTerm && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: 'var(--color-tint-blue)',
                color: 'var(--color-primary-700)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                {t('scans.search', 'Search')}: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary-700)',
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '0.25rem'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: 'var(--color-tint-blue)',
                color: 'var(--color-primary-700)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                {t('scans.status', 'Status')}: {statusFilter}
                <button
                  onClick={() => setStatusFilter('all')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary-700)',
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '0.25rem'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}
            {projectFilter !== 'all' && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: 'var(--color-tint-blue)',
                color: 'var(--color-primary-700)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                {t('scans.project', 'Project')}: {projectFilter}
                <button
                  onClick={() => setProjectFilter('all')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary-700)',
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '0.25rem'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}
            {scanTypeFilter !== 'all' && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: scanTypeFilter === 'vulnerability' ? 'var(--color-tint-blue)' :
                               scanTypeFilter === 'recon' ? 'var(--color-tint-purple)' : 'var(--color-tint-yellow)',
                color: scanTypeFilter === 'vulnerability' ? 'var(--color-primary-700, #1e40af)' :
                      scanTypeFilter === 'recon' ? 'var(--color-purple-text, #7c3aed)' : 'var(--color-warning-text, #b45309)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                Type: {scanTypeFilter === 'vulnerability' ? 'Vuln Scan' :
                      scanTypeFilter === 'recon' ? 'Recon' : 'Discovery'}
                <button
                  onClick={() => setScanTypeFilter('all')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: scanTypeFilter === 'vulnerability' ? 'var(--color-primary-700, #1e40af)' :
                          scanTypeFilter === 'recon' ? 'var(--color-purple-text, #7c3aed)' : 'var(--color-warning-text, #b45309)',
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '0.25rem'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setProjectFilter('all');
                setScanTypeFilter('all');
              }}
              style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '0.25rem 0.5rem'
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Scans List */}
      {filteredScans.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          backgroundColor: 'var(--color-background)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-primary)'
        }}>
          <i className="fas fa-search" style={{
            fontSize: '4rem',
            marginBottom: '1.5rem',
            color: 'var(--color-border-secondary)'
          }}></i>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            color: 'var(--color-text-secondary)',
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            {scans.length === 0 ? 'No scans found' : 'No matching scans'}
          </h3>
          <p style={{
            color: 'var(--color-text-tertiary)',
            margin: '0 0 2rem 0',
            fontSize: '0.875rem'
          }}>
            {scans.length === 0
              ? (projectId
                  ? 'Create your first scan to start identifying security vulnerabilities in this project'
                  : 'No scans have been created yet. Create a scan within a project to get started.')
              : 'Try adjusting your search term or filters to find what you\'re looking for.'
            }
          </p>
          {scans.length === 0 && !projectId && (
            <Link
              to="/projects"
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: 'var(--color-primary-500)',
                color: 'var(--color-text-inverse)',
                textDecoration: 'none',
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              <i className="fas fa-folder-open"></i>
              View Projects
            </Link>
          )}
          {scans.length > 0 && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setProjectFilter('all');
              }}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: 'var(--color-primary-500)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '600',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times-circle"></i>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredScans.map(scan => (
            <div
              key={scan.id}
              onClick={() => {
                // Handle different scan types with different routes
                const scanType = (scan as any).scan_type;
                if (scanType === 'recon') {
                  navigate(`/recon/scans/${scan.id}`);
                } else if (scanType === 'discovery') {
                  // Navigate to discovery page with scan ID as query param to auto-select
                  navigate(`/projects/${projectId}/discovery?scanId=${scan.id}`);
                } else {
                  // Set the selected scan in context when in project context
                  if (projectId) {
                    setSelectedScan(scan);
                    navigate(`/projects/${projectId}/scans/${scan.id}`);
                  } else {
                    navigate(`/scans/${scan.id}`);
                  }
                }
              }}
              style={{
                backgroundColor: 'var(--color-background)',
                border: selectedScans.has(scan.id) ? '2px solid var(--color-primary-500)' : '1px solid var(--color-border-primary)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: 'var(--color-card-shadow)',
                textDecoration: 'none',
                color: 'inherit'
              }}
              onMouseOver={(e) => {
                if (!selectedScans.has(scan.id)) {
                  e.currentTarget.style.borderColor = 'var(--color-primary-500)';
                }
                e.currentTarget.style.boxShadow = 'var(--color-card-shadow)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                if (!selectedScans.has(scan.id)) {
                  e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                } else {
                  e.currentTarget.style.borderColor = 'var(--color-primary-500)';
                }
                e.currentTarget.style.boxShadow = '0 1px 3px var(--color-card-shadow)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  {/* Checkbox */}
                  <div onClick={(e) => {
                    e.stopPropagation();
                    toggleScanSelection(scan.id);
                  }} style={{ paddingTop: '0.25rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedScans.has(scan.id)}
                      onChange={() => toggleScanSelection(scan.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '1.125rem', 
                        fontWeight: '600', 
                        color: 'var(--color-text-primary)'
                      }}>
                        {scan.name}
                      </h3>
                      
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '12px',
                          backgroundColor: getStatusColor(scan.status) + '20',
                          color: getStatusColor(scan.status),
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        <i className={getStatusIcon(scan.status)}></i>
                        {scan.status.toUpperCase()}
                      </span>

                      {/* Scan Type Badge */}
                      {(scan as any).scan_type && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '12px',
                            backgroundColor: (scan as any).scan_type === 'vulnerability' ? 'var(--color-tint-blue)' :
                                           (scan as any).scan_type === 'recon' ? 'var(--color-tint-purple)' : 'var(--color-tint-yellow)',
                            color: (scan as any).scan_type === 'vulnerability' ? 'var(--color-primary-700, #1e40af)' :
                                  (scan as any).scan_type === 'recon' ? 'var(--color-purple-text, #7c3aed)' : 'var(--color-warning-text, #b45309)',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase' as const
                          }}
                        >
                          <i className={
                            (scan as any).scan_type === 'vulnerability' ? 'fas fa-shield-alt' :
                            (scan as any).scan_type === 'recon' ? 'fas fa-satellite-dish' : 'fas fa-network-wired'
                          } style={{ fontSize: '0.65rem' }}></i>
                          {(scan as any).scan_type === 'vulnerability' ? 'Vuln Scan' :
                           (scan as any).scan_type === 'recon' ? 'Recon' : 'Discovery'}
                        </span>
                      )}

                      {/* Risk Grade Badge */}
                      {scan.risk_grade && (
                        <RiskGradeBadge grade={scan.risk_grade} score={scan.risk_score} size="small" />
                      )}

                      {scan.progress !== undefined && scan.progress > 0 && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-text-tertiary)',
                          fontWeight: '500'
                        }}>
                          {scan.progress}%
                        </span>
                      )}
                    </div>

                    {/* Project name (when viewing all scans) */}
                    {!projectId && (scan as any).project_name && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                      }}>
                        <i className="fas fa-folder" style={{ color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}></i>
                        <span style={{ 
                          color: 'var(--color-text-tertiary)',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          {(scan as any).project_name}
                        </span>
                      </div>
                    )}

                    {scan.description && (
                      <p style={{ 
                        color: 'var(--color-text-tertiary)',
                        margin: '0 0 0.75rem 0',
                        fontSize: '0.875rem'
                      }}>
                        {scan.description}
                      </p>
                    )}

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      fontSize: '0.75rem',
                      color: 'var(--color-text-tertiary)',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <i className="fas fa-calendar"></i>
                        {new Date(scan.created_at).toLocaleDateString()}
                        {' '}
                        {/* Show start time for running scans, end time for completed scans */}
                        {scan.status === 'running' && scan.started_at ? (
                          <span style={{ color: 'var(--color-primary-500)' }}>
                            {new Date(scan.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : scan.status === 'completed' && scan.completed_at ? (
                          <span>
                            {new Date(scan.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : scan.started_at ? (
                          <span>
                            {new Date(scan.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : null}
                      </span>

                      {/* Scan Duration - only show for completed scans or actively running scans */}
                      {scan.started_at && (scan.completed_at || scan.status === 'running') && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <i className="fas fa-clock"></i>
                          {(() => {
                            const startTime = new Date(scan.started_at).getTime();
                            // For completed scans, use completed_at; for running scans, use current time
                            const endTime = scan.completed_at
                              ? new Date(scan.completed_at).getTime()
                              : (scan.status === 'running' ? Date.now() : null);

                            if (!endTime) return '--';

                            const duration = Math.floor((endTime - startTime) / 1000);
                            if (duration < 0) return '--'; // Guard against invalid times
                            if (duration < 60) return `${duration}s`;
                            if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
                            return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
                          })()}
                        </span>
                      )}

                      {scan.status === 'running' && scan.progress !== undefined && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--color-primary-500)', fontWeight: '500' }}>
                          {scan.progress}% complete
                        </span>
                      )}

                      {/* Severity breakdown badges - same as Overview page */}
                      {(scan as any).results_count !== undefined && (scan as any).results_count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-secondary)' }}>
                            {(scan as any).results_count} issues:
                          </span>
                          {(scan as any).critical_count > 0 && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--color-tint-red)',
                              color: 'var(--color-critical)',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {(scan as any).critical_count} Critical
                            </span>
                          )}
                          {(scan as any).high_count > 0 && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--color-tint-yellow)',
                              color: 'var(--color-warning-dark)',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {(scan as any).high_count} High
                            </span>
                          )}
                          {(scan as any).medium_count > 0 && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--color-tint-yellow)',
                              color: 'var(--color-warning-dark)',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {(scan as any).medium_count} Medium
                            </span>
                          )}
                          {(scan as any).low_count > 0 && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--color-tint-green)',
                              color: 'var(--color-success-dark)',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {(scan as any).low_count} Low
                            </span>
                          )}
                          {(scan as any).info_count > 0 && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--color-tint-blue)',
                              color: 'var(--color-primary-600)',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {(scan as any).info_count} Info
                            </span>
                          )}
                        </div>
                      )}
                      {/* Fallback for old API format */}
                      {(scan as any).results_count === undefined && ((scan as any).result_count > 0 || (scan.results && scan.results.length > 0)) && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--color-error)', fontWeight: '500' }}>
                          <i className="fas fa-bug"></i>
                          {(scan as any).result_count || scan.results?.length || 0} findings
                        </span>
                      )}
                    </div>

                    {/* Target count on separate line */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      fontSize: '0.75rem',
                      color: 'var(--color-text-muted)',
                      marginTop: '0.5rem'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <i className="fas fa-bullseye"></i>
                        {(scan as any).target_count || scan.targets?.length || 0} targets
                      </span>
                    </div>

                    {/* Special status messages */}
                    {scan.status === 'interrupted' && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: 'var(--color-tint-yellow)',
                        border: '1px solid var(--color-tint-yellow)',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: 'var(--color-warning-dark)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <i className="fas fa-info-circle"></i>
                        This scan was interrupted and can be resumed
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                    {getActionButtons(scan)}
                  </div>
                </div>

                {/* Progress Bar for running scans */}
                {scan.status === 'running' && scan.progress !== undefined && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'var(--color-border-primary)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div
                        style={{
                          width: `${scan.progress}%`,
                          height: '100%',
                          backgroundColor: 'var(--color-primary-500)',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                  </div>
                )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          padding: '1rem 1.5rem',
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} scans
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>Per page:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1); // Reset to first page when changing page size
                }}
                style={{
                  padding: '0.5rem 2rem 0.5rem 0.75rem',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--color-background)',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.25em 1.25em'
                }}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '6px',
                backgroundColor: page === 1 ? 'var(--color-background-tertiary)' : 'var(--color-background)',
                color: page === 1 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem'
              }}
              title="First page"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '6px',
                backgroundColor: page === 1 ? 'var(--color-background-tertiary)' : 'var(--color-background)',
                color: page === 1 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem'
              }}
              title="Previous page"
            >
              <i className="fas fa-angle-left"></i>
            </button>

            <span style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              fontWeight: '500'
            }}>
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '6px',
                backgroundColor: page === totalPages ? 'var(--color-background-tertiary)' : 'var(--color-background)',
                color: page === totalPages ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem'
              }}
              title="Next page"
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '6px',
                backgroundColor: page === totalPages ? 'var(--color-background-tertiary)' : 'var(--color-background)',
                color: page === totalPages ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem'
              }}
              title="Last page"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Page size selector when only one page but many results */}
      {totalPages === 1 && totalCount > 25 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '1rem',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>
            Showing {totalCount} scans
          </span>
          <label style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginLeft: '1rem' }}>Per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            style={{
              padding: '0.5rem 2rem 0.5rem 0.75rem',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: 'var(--color-background)',
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25em 1.25em'
            }}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      )}

      {/* Scan Comparison Modal */}
      {showComparison && selectedScans.size >= 2 && (
        <ScanComparison
          projectId={projectId ? parseInt(projectId) : filteredScans.find(s => selectedScans.has(s.id))?.project_id || 0}
          scanIds={Array.from(selectedScans)}
          onClose={() => {
            setShowComparison(false);
          }}
        />
      )}
    </div>
  );
};

export default Scans;